/*
 * useResolvedCardUrls (v242.37.0) — resolve saved card image refs to viewable URLs.
 *
 * A card ref persisted to the profile keeps a durable `storagePath` plus a `url`
 * that was a presigned S3 GET captured at UPLOAD time. Presigned URLs expire, so
 * on any later profile reload the saved `url` returns 403 and the <img> renders
 * broken ("?"). This hook re-presigns the durable `storagePath` fresh on read.
 *
 * Resolution order per card:
 *   1. fresh in-session capture (base64 `data`) — render immediately, no round-trip
 *   2. durable `storagePath` — presign fresh via resolveFileUrl
 *   3. `url` fallback — last resort (may be a fresh signed URL or already stale)
 *
 * @param {Object} cards  map of key -> card ref ({ data?, url?, storagePath? } | null)
 * @returns {Object}      map of key -> resolved viewable URL (or null while pending)
 */
import { useState, useEffect } from 'react'
import { resolveFileUrl } from '../services/api'
import { resolveCardSrc, cardStorageKey } from '../utils/cardRef'

export default function useResolvedCardUrls(cards) {
  const [urls, setUrls] = useState({})
  const map = cards || {}
  const keys = Object.keys(map)

  // Stable dependency: re-run only when a card's durable ref actually changes.
  const sig = keys
    .map((k) => {
      const c = map[k]
      return c ? `${k}:${cardStorageKey(c) || ''}|${typeof c === 'string' ? c : (c.url || '')}|${c.data ? 'd' : ''}` : `${k}:`
    })
    .join(';')

  useEffect(() => {
    let cancelled = false

    // PROD-793: storagePath, a bare key or a url that is a key all go through the signer;
    // a failed signing resolves to null (placeholder), never to the raw key.
    const resolveOne = (c) => resolveCardSrc(c, resolveFileUrl)

    ;(async () => {
      const entries = await Promise.all(keys.map(async (k) => [k, await resolveOne(map[k])]))
      if (!cancelled) setUrls(Object.fromEntries(entries))
    })()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig])

  return urls
}
